/**
 * Multi-Produkt-Import-Modal.
 *
 * Workflow:
 *   1. Marketer fügt eine Partner-Übersichts-URL ein (z. B. `https://allyver.com/seedos`).
 *   2. Wir rufen `/api/partner-extract` und zeigen alle erkannten Produkte/
 *      Dienstleistungen mit Bild, Name und Preis.
 *   3. Per Checkbox + Pfeil-Buttons wählt der Marketer aus, welche Items
 *      welcher Reihenfolge als `partnerServiceDetail`-Szenen erzeugt werden
 *      sollen.
 *   4. Beim Bestätigen rufen wir für jedes selektierte Item parallel
 *      `/api/partner-product-extract?url=<item.url>&mirror=1` auf und bauen
 *      daraus N Szenen. Der Parent (Composer) hängt sie an die Timeline.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  api,
  type PartnerData,
  type PartnerItem,
  type ProductData,
  type ReelScene,
  type SceneMeta,
} from "../api";

type Props = {
  open: boolean;
  onClose: () => void;
  /** Catalog meta for `partnerServiceDetail` — needed for defaults (duration, accent, …). */
  detailMeta: SceneMeta | null;
  /** Insert the prepared scenes (already filled with product data + sensible
   *  defaults; uid + fromSec are assigned by the parent). */
  onInsert: (scenes: ReelScene[]) => void;
};

type Row = {
  /** Index of the item in `partner.items` (stable identity for the checkbox). */
  key: string;
  item: PartnerItem;
  selected: boolean;
  /** Position in the FINAL scene order (0..N-1). Filled when `selected`. */
  order: number | null;
};

const SAMPLE_URLS = [
  "https://allyver.com/seedos",
  "https://allyver.com/dekomi",
];

function newUid(): string {
  return `s${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

function productToSceneInputs(
  product: ProductData,
  fallback: PartnerItem | null,
  partner: PartnerData | null,
): ReelScene["inputs"] {
  const sellerName = product.sellerName || partner?.name || "";
  const locationLabel = product.location || partner?.location || "";
  const productImg = product.imageUrl || fallback?.imageUrl;
  const merchantLogo = partner?.logoUrl;
  const categoryTag = product.categoryTag || "PRODUKT";
  return {
    text: product.name,
    subtext: sellerName,
    url: product.displayUrl,
    accent: "primary",
    media: [
      ...(productImg ? [productImg] : []),
      ...(merchantLogo ? [merchantLogo] : []),
    ],
    extras: {
      productUrl: product.sourceUrl,
      price: product.priceLabel ?? fallback?.priceLabel ?? "",
      location: locationLabel,
      description: product.description ?? "",
      ctaText: "Jetzt anfragen",
      kategorieType: categoryTag,
      imageFit: "contain",
      sfx: true,
    },
  };
}

export const PartnerProductsImportModal: React.FC<Props> = ({
  open,
  onClose,
  detailMeta,
  onInsert,
}) => {
  const [url, setUrl] = useState<string>("");
  const [partner, setPartner] = useState<PartnerData | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [perSceneSec, setPerSceneSec] = useState<number>(() => detailMeta?.defaultDurationSec ?? 7);

  useEffect(() => {
    if (!open) {
      setUrl("");
      setPartner(null);
      setRows([]);
      setErr(null);
      setHint(null);
    }
  }, [open]);

  useEffect(() => {
    if (detailMeta?.defaultDurationSec) setPerSceneSec(detailMeta.defaultDurationSec);
  }, [detailMeta]);

  const selectedCount = rows.filter((r) => r.selected).length;
  const orderedRows = useMemo(
    () =>
      rows
        .filter((r) => r.selected && r.order !== null)
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [rows],
  );

  if (!open) return null;

  const resequence = (next: Row[]): Row[] => {
    const sorted = next
      .filter((r) => r.selected)
      .sort((a, b) => (a.order ?? 99999) - (b.order ?? 99999));
    const orderMap = new Map(sorted.map((r, i) => [r.key, i]));
    return next.map((r) =>
      r.selected ? { ...r, order: orderMap.get(r.key) ?? 0 } : { ...r, order: null },
    );
  };

  const loadPartner = async (): Promise<void> => {
    const trimmed = url.trim();
    if (!trimmed) {
      setErr("Bitte URL eingeben.");
      return;
    }
    setLoading(true);
    setErr(null);
    setHint(null);
    setPartner(null);
    setRows([]);
    try {
      const data = await api.partnerExtract(trimmed);
      setPartner(data);

      // Safety-net dedup: even if the backend leaves `&amp;`-vs-`&` duplicates
      // through, collapse them by normalized name (decode entities + lower
      // + collapse whitespace). Prefer the entry that carries the richer
      // payload (image > url > priceLabel) when both exist.
      const decode = (s: string): string =>
        s
          .replace(/&amp;/g, "&")
          .replace(/&lt;/g, "<")
          .replace(/&gt;/g, ">")
          .replace(/&quot;/g, '"')
          .replace(/&#39;|&apos;/g, "'");
      const score = (it: PartnerItem): number =>
        (it.imageUrl ? 4 : 0) + (it.url ? 2 : 0) + (it.priceLabel ? 1 : 0);
      const byKey = new Map<string, PartnerItem>();
      for (const it of data.items) {
        const cleaned: PartnerItem = { ...it, name: decode(it.name).trim() };
        const k = cleaned.name.toLowerCase().replace(/\s+/g, " ");
        if (!k) continue;
        const prev = byKey.get(k);
        if (!prev || score(cleaned) > score(prev)) byKey.set(k, cleaned);
      }
      const cleaned = Array.from(byKey.values());

      const initial: Row[] = cleaned.map((it, i) => ({
        key: `${i}-${it.name}`,
        item: it,
        selected: false,
        order: null,
      }));
      setRows(initial);
      setHint(
        `${cleaned.length} ${cleaned.length === 1 ? "Angebot" : "Angebote"} erkannt bei ${data.name}${
          data.items.length !== cleaned.length ? ` (${data.items.length - cleaned.length} Duplikate entfernt)` : ""
        }.`,
      );
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (key: string): void => {
    setRows((prev) => {
      const idx = prev.findIndex((r) => r.key === key);
      if (idx < 0) return prev;
      const cur = prev[idx];
      const next = prev.slice();
      if (cur.selected) {
        next[idx] = { ...cur, selected: false, order: null };
      } else {
        const nextOrder = prev.filter((r) => r.selected).length;
        next[idx] = { ...cur, selected: true, order: nextOrder };
      }
      return resequence(next);
    });
  };

  const move = (key: string, dir: -1 | 1): void => {
    setRows((prev) => {
      const sorted = prev
        .filter((r) => r.selected)
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const idx = sorted.findIndex((r) => r.key === key);
      if (idx < 0) return prev;
      const swap = idx + dir;
      if (swap < 0 || swap >= sorted.length) return prev;
      [sorted[idx], sorted[swap]] = [sorted[swap], sorted[idx]];
      const orderMap = new Map(sorted.map((r, i) => [r.key, i]));
      return prev.map((r) =>
        r.selected ? { ...r, order: orderMap.get(r.key) ?? 0 } : r,
      );
    });
  };

  const selectAll = (): void => {
    setRows((prev) =>
      resequence(
        prev.map((r, i) => ({ ...r, selected: true, order: i })),
      ),
    );
  };

  const clearAll = (): void => {
    setRows((prev) => prev.map((r) => ({ ...r, selected: false, order: null })));
  };

  const importSelected = async (): Promise<void> => {
    if (!detailMeta) {
      setErr("Scene-Meta fehlt — bitte Composer neu laden.");
      return;
    }
    if (orderedRows.length === 0) {
      setErr("Mindestens ein Angebot auswählen.");
      return;
    }
    setBusy(true);
    setErr(null);
    setHint(null);
    try {
      // Sequentiell um Allyver nicht zu fluten + Reihenfolge stabil zu halten.
      const fetched: Array<{ product: ProductData; fallback: PartnerItem }> = [];
      for (const row of orderedRows) {
        const itemUrl = row.item.url;
        if (!itemUrl) {
          // Item ohne eigene Detail-URL: wir bauen die Szene direkt aus dem
          // Listing-Eintrag (Bild + Preis + Name reichen für eine Detail-Szene).
          fetched.push({
            product: {
              sourceUrl: partner?.sourceUrl ?? url.trim(),
              displayUrl: partner?.displayUrl ?? "",
              slug: undefined,
              name: row.item.name,
              priceLabel: row.item.priceLabel,
              priceValue: row.item.priceValue,
              priceCurrency: row.item.priceCurrency,
              description: undefined,
              sellerName: partner?.name,
              location: partner?.location,
              city: partner?.city,
              postalCode: partner?.postalCode,
              imageUrl: row.item.imageUrl,
              categoryTag: row.item.kind.toUpperCase(),
              shopUrl: undefined,
            },
            fallback: row.item,
          });
          continue;
        }
        try {
          const product = await api.partnerProductExtract(itemUrl, { mirror: true });
          fetched.push({ product, fallback: row.item });
        } catch (e) {
          console.warn(`[partner-products] fetch fail for ${itemUrl}:`, (e as Error).message);
          // Skip silent — UI behaviour for partial failures is "do best effort".
        }
      }

      const newScenes: ReelScene[] = fetched.map(({ product, fallback }) => ({
        uid: newUid(),
        sceneId: "partnerServiceDetail",
        durationSec: perSceneSec,
        naturalDurationSec: detailMeta.defaultDurationSec,
        inputs: productToSceneInputs(product, fallback, partner),
        transition: { kind: "none", durationFrames: 0 },
      }));

      if (newScenes.length === 0) {
        setErr("Keine Detail-Daten geladen.");
        return;
      }

      onInsert(newScenes);
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal partner-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h2>🛒 Produkt-Detail-Szenen aus Partner-URL</h2>
            <p className="small muted">
              URL einer Allyver-Partnerseite einfügen → Produkte/Dienstleistungen
              auswählen, Reihenfolge festlegen → wir bauen für jeden Eintrag
              eine eigene „Partner Service-Detail“-Szene und hängen sie ans Reel.
            </p>
          </div>
          <button className="btn ghost" onClick={onClose} disabled={busy}>
            ✕
          </button>
        </div>

        <div className="partner-row">
          <label className="partner-label">Partner-URL (Übersicht)</label>
          <div className="partner-input-row">
            <input
              type="url"
              className="partner-input"
              placeholder="https://allyver.com/seedos"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void loadPartner();
              }}
              disabled={loading || busy}
              autoFocus
            />
            <button
              className="btn primary"
              onClick={() => void loadPartner()}
              disabled={loading || busy || !url.trim()}
            >
              {loading ? "…" : "Produkte laden"}
            </button>
          </div>
          <div className="partner-samples">
            {SAMPLE_URLS.map((s) => (
              <button
                key={s}
                className="partner-sample"
                onClick={() => setUrl(s)}
                disabled={loading || busy}
                type="button"
              >
                {s.replace(/^https?:\/\//, "")}
              </button>
            ))}
          </div>
        </div>

        {err && <div className="warn-banner">{err}</div>}
        {hint && !err && <div className="small muted" style={{ marginTop: 4 }}>{hint}</div>}

        {partner && rows.length > 0 && (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginTop: 10,
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div className="partner-items-head">
                <strong>{selectedCount}</strong> von <strong>{rows.length}</strong> ausgewählt
                {selectedCount > 0 && (
                  <span className="small muted" style={{ marginLeft: 8 }}>
                    · Reihenfolge: ↑/↓ rechts an der Karte
                  </span>
                )}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button type="button" className="btn ghost sm" onClick={selectAll} disabled={busy}>
                  Alle auswählen
                </button>
                <button type="button" className="btn ghost sm" onClick={clearAll} disabled={busy}>
                  Auswahl leeren
                </button>
              </div>
            </div>

            <div
              style={{
                marginTop: 10,
                maxHeight: 380,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              {[...rows]
                .sort((a, b) => {
                  if (a.selected && b.selected) return (a.order ?? 0) - (b.order ?? 0);
                  if (a.selected) return -1;
                  if (b.selected) return 1;
                  return 0;
                })
                .map((r) => {
                  return (
                    <ProductRow
                      key={r.key}
                      row={r}
                      onToggle={() => toggleSelect(r.key)}
                      onMoveUp={() => move(r.key, -1)}
                      onMoveDown={() => move(r.key, 1)}
                      first={r.order === 0}
                      last={r.order === selectedCount - 1}
                    />
                  );
                })}
            </div>

            <div className="partner-mode-row" style={{ marginTop: 12 }}>
              <label className="form-row" style={{ flex: 1 }}>
                <span>Dauer pro Detail-Szene (s)</span>
                <input
                  type="number"
                  min={3}
                  max={20}
                  step={0.5}
                  value={perSceneSec}
                  onChange={(e) =>
                    setPerSceneSec(Math.max(3, Math.min(20, Number(e.target.value) || 7)))
                  }
                />
              </label>
              <div className="small muted" style={{ alignSelf: "flex-end" }}>
                Gesamt: ~{(perSceneSec * selectedCount).toFixed(1)}s zusätzliche Timeline.
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn ghost" onClick={onClose} disabled={busy}>
                Abbrechen
              </button>
              <button
                className="btn primary"
                onClick={() => void importSelected()}
                disabled={busy || selectedCount === 0}
              >
                {busy
                  ? "Lade Details …"
                  : `${selectedCount} Detail-Szenen hinzufügen`}
              </button>
            </div>
          </>
        )}

        {partner && rows.length === 0 && (
          <div className="small muted" style={{ marginTop: 10 }}>
            Keine Angebote auf dieser Partner-Seite gefunden — bitte eine
            Übersichts-URL nutzen (nicht eine einzelne Produkt-URL).
          </div>
        )}
      </div>
    </div>
  );
};

const ProductRow: React.FC<{
  row: Row;
  onToggle: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  first: boolean;
  last: boolean;
}> = ({ row, onToggle, onMoveUp, onMoveDown, first, last }) => {
  const it = row.item;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: 8,
        background: row.selected
          ? "rgba(34, 197, 94, 0.08)"
          : "rgba(255,255,255,0.02)",
        border: row.selected
          ? "1px solid rgba(34, 197, 94, 0.35)"
          : "1px solid rgba(255,255,255,0.08)",
        borderRadius: 10,
      }}
    >
      <input
        type="checkbox"
        checked={row.selected}
        onChange={onToggle}
        style={{ width: 18, height: 18, cursor: "pointer" }}
      />
      {row.selected ? (
        <div
          style={{
            minWidth: 22,
            height: 22,
            borderRadius: 11,
            background: "rgba(34, 197, 94, 0.85)",
            color: "#0a0e15",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {(row.order ?? 0) + 1}
        </div>
      ) : (
        <div style={{ width: 22 }} />
      )}
      {it.imageUrl ? (
        <img
          src={it.imageUrl}
          alt={it.name}
          style={{
            width: 56,
            height: 56,
            borderRadius: 8,
            objectFit: "cover",
            background: "rgba(255,255,255,0.06)",
          }}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
      ) : (
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 8,
            background: "rgba(255,255,255,0.06)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
          }}
        >
          {it.kind.charAt(0)}
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {it.name}
        </div>
        <div className="small muted">
          {it.priceLabel && it.priceLabel !== "NaN" ? `${it.priceLabel} · ` : ""}
          {it.kind}
          {it.url ? "" : " · (kein Detail-Link)"}
        </div>
      </div>
      {row.selected ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <button
            type="button"
            className="btn ghost sm"
            onClick={onMoveUp}
            disabled={first}
            title="Nach oben"
            style={{ padding: "2px 8px" }}
          >
            ↑
          </button>
          <button
            type="button"
            className="btn ghost sm"
            onClick={onMoveDown}
            disabled={last}
            title="Nach unten"
            style={{ padding: "2px 8px" }}
          >
            ↓
          </button>
        </div>
      ) : null}
    </div>
  );
};
